import { useState, useRef, useEffect } from 'react';
import { StudentRecord } from '@/mocks/students';

interface StudentMultiSelectProps {
  students: StudentRecord[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  noResultsText?: string;
  selectedCountText?: string;
  singleSelect?: boolean;
}

export default function StudentMultiSelect({
  students,
  selectedIds,
  onChange,
  placeholder = 'Select students...',
  noResultsText = 'No students found',
  selectedCountText = '{{count}} selected',
  singleSelect = false,
}: StudentMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = students.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id: string) => {
    if (singleSelect) {
      onChange(selectedIds.includes(id) ? [] : [id]);
      return;
    }
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  const selectAll = () => onChange(students.map((s) => s.id));
  const clearAll = () => onChange([]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 xl-input text-left cursor-pointer"
      >
        <span className={selectedIds.length > 0 ? 'text-foreground-900' : 'text-foreground-400'}>
          {selectedIds.length > 0
            ? selectedCountText.replace('{{count}}', String(selectedIds.length))
            : placeholder}
        </span>
        <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1.5 w-full bg-background-50 border border-background-200 rounded-xl shadow-floating overflow-hidden">
          <div className="p-2 border-b border-background-100">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-9 pr-3 py-1.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
          </div>
          {!singleSelect && (
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-background-100">
              <button
                type="button"
                onClick={selectAll}
                className="text-xs text-primary-600 font-label font-medium hover:text-primary-700 cursor-pointer"
              >
                Select All
              </button>
              <span className="text-foreground-300 text-xs">|</span>
              <button
                type="button"
                onClick={clearAll}
                className="text-xs text-foreground-500 font-label font-medium hover:text-foreground-700 cursor-pointer"
              >
                Clear
              </button>
            </div>
          )}
          <div className="max-h-52 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="text-sm text-foreground-400 text-center py-4">{noResultsText}</p>
            ) : (
              filtered.map((s) => {
                const isSelected = selectedIds.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggle(s.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors cursor-pointer ${
                      isSelected ? 'bg-primary-50' : 'hover:bg-background-100'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 flex items-center justify-center rounded border-2 flex-shrink-0 transition-colors ${
                        isSelected
                          ? 'bg-primary-500 border-primary-500 text-background-50'
                          : 'border-background-300'
                      }`}
                    >
                      {isSelected && <i className="ri-check-line text-xs" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-900 truncate">{s.name}</p>
                      <p className="text-xs text-foreground-500 truncate">
                        {s.className} &middot; {s.studentId}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}